import React, { useState, useEffect } from 'react';
import { Save, Plus, Edit, Trash2 } from 'lucide-react';
import { User } from '../../models/User';
import { db } from '../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface FinancialData {
  gross: number;
  net: number;
  opex: number;
  ebitda: number;
}

interface YearData {
  [month: string]: FinancialData;
}

interface AdminManagementAccountProps {
  user: User;
}

const AdminManagementAccount: React.FC<AdminManagementAccountProps> = ({ user }) => {
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const years = [2025, 2024, 2023, 2022, 2021, 2020];
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const [financialData, setFinancialData] = useState<{ [year: number]: YearData }>({
    2025: months.reduce((acc, month) => {
      acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
      return acc;
    }, {} as YearData),
    2024: months.reduce((acc, month) => {
      acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
      return acc;
    }, {} as YearData),
    2023: months.reduce((acc, month) => {
      acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
      return acc;
    }, {} as YearData),
    2022: months.reduce((acc, month) => {
      acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
      return acc;
    }, {} as YearData),
    2021: months.reduce((acc, month) => {
      acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
      return acc;
    }, {} as YearData),
    2020: months.reduce((acc, month) => {
      acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
      return acc;
    }, {} as YearData),
  });

  // Load data from Firestore on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
  
        const docRef = doc(db, 'managementAccounts', 'main');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
  
          setFinancialData(docSnap.data().financialData);
        } else {
  
        }
      } catch (error) {
        console.error('Error loading financial data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (year: number, month: string, field: keyof FinancialData, value: string) => {
    const numValue = parseFloat(value) || 0;
    setFinancialData(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [month]: {
          ...prev[year][month],
          [field]: numValue
        }
      }
    }));
  };

  const formatNumber = (value: number) => {
    if (value === 0) return '';
    const formatted = Math.abs(value).toLocaleString();
    return value >= 0 ? formatted : `(${formatted})`;
  };

  const getNumberColor = (value: number) => {
    return value >= 0 ? 'text-blue-600' : 'text-red-600';
  };

  const calculateSummary = (year: number, field: keyof FinancialData) => {
    const currentYear = new Date().getFullYear();
    const yearData = financialData[year];
    if (!yearData) return 0;
    
    if (year === currentYear) {
      // YTD: sum only months that have content (non-zero values)
      return Object.values(yearData).reduce((sum, monthData) => {
        return sum + (monthData[field] !== 0 ? monthData[field] : 0);
      }, 0);
    } else {
      // FY: sum all months
      return Object.values(yearData).reduce((sum, monthData) => {
        return sum + monthData[field];
      }, 0);
    }
  };

  const getSummaryLabel = (year: number) => {
    const currentYear = new Date().getFullYear();
    return year === currentYear ? `YTD ${year}` : `FY ${year}`;
  };

  const handleSave = async () => {
    try {

      const docRef = doc(db, 'managementAccounts', 'main');
      const dataToSave = {
        financialData,
        lastUpdated: new Date().toISOString(),
        updatedBy: user.id
      };
      await setDoc(docRef, dataToSave);

      setIsEditing(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving financial data:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const renderTable = (year: number) => (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Metric
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100 border-l border-r border-gray-200">
                {getSummaryLabel(year)}
              </th>
              {months.map((month) => (
                <th key={month} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                Gross
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                <span className={getNumberColor(calculateSummary(year, 'gross'))}>
                  {formatNumber(calculateSummary(year, 'gross'))}
                </span>
              </td>
              {months.map((month) => (
                <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {isEditing ? (
                    <input
                      type="number"
                      value={financialData[year][month].gross}
                      onChange={(e) => handleInputChange(year, month, 'gross', e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <span className={getNumberColor(financialData[year][month].gross)}>
                      {formatNumber(financialData[year][month].gross)}
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                Net
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                <span className={getNumberColor(calculateSummary(year, 'net'))}>
                  {formatNumber(calculateSummary(year, 'net'))}
                </span>
              </td>
              {months.map((month) => (
                <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {isEditing ? (
                    <input
                      type="number"
                      value={financialData[year][month].net}
                      onChange={(e) => handleInputChange(year, month, 'net', e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <span className={getNumberColor(financialData[year][month].net)}>
                      {formatNumber(financialData[year][month].net)}
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                OPEX
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                <span className={getNumberColor(calculateSummary(year, 'opex'))}>
                  {formatNumber(calculateSummary(year, 'opex'))}
                </span>
              </td>
              {months.map((month) => (
                <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {isEditing ? (
                    <input
                      type="number"
                      value={financialData[year][month].opex}
                      onChange={(e) => handleInputChange(year, month, 'opex', e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <span className={getNumberColor(financialData[year][month].opex)}>
                      {formatNumber(financialData[year][month].opex)}
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                EBITDA
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-100 border-l border-r border-gray-200">
                <span className={getNumberColor(calculateSummary(year, 'ebitda'))}>
                  {formatNumber(calculateSummary(year, 'ebitda'))}
                </span>
              </td>
              {months.map((month) => (
                <td key={month} className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  {isEditing ? (
                    <input
                      type="number"
                      value={financialData[year][month].ebitda}
                      onChange={(e) => handleInputChange(year, month, 'ebitda', e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <span className={getNumberColor(financialData[year][month].ebitda)}>
                      {formatNumber(financialData[year][month].ebitda)}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Management Accounts</h3>
          <p className="text-sm text-gray-600">Edit financial performance data by year and month</p>
        </div>
        <div className="flex space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Data
            </button>
          )}
        </div>
      </div>
      {loading && <div className="text-blue-600">Loading data...</div>}
      {saveStatus === 'success' && <div className="text-green-600">Saved successfully!</div>}
      {saveStatus === 'error' && <div className="text-red-600">Error saving data.</div>}
      {/* Year Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-4 sm:space-x-8 border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`py-3 px-3 sm:px-1 border-b-2 font-medium text-sm transition-all duration-200 whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation ${
                selectedYear === year
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {year}
            </button>
          ))}
        </nav>
      </div>
      {/* Financial Table */}
      {renderTable(selectedYear)}
    </div>
  );
};

export default AdminManagementAccount; 